import { Center, Loader, rem } from '@mantine/core';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import { useNavigate, useSearchParams } from 'react-router';
import { briefingPostHeading, dayExtent, staffs } from '@reditelji/shared';
import { useChangelog, useEvent, useRaceRoutes, useShiftMutations } from '../api/hooks';
import { entryTitle } from '../changelog/ChangelogDrawer';
import {
  useAllShifts,
  usePostMutations,
  usePosts,
  usePostTagsMutation,
  useSectors,
  useTranslateInstructions,
  type PostRow,
} from '../api/posts';
import { useExternalGroups } from '../api/externalGroups';
import { useSession } from '../api/session';
import { useTags } from '../api/tags';
import { useVolunteers } from '../api/volunteers';
import { TimeField } from '../components/TimeField';
import type { EventDay, Shift } from '../api/types';
import { covTone, dayAcc, dayFrom, dayToneAt, dur, m2t, plS } from '../lib/ui';
import { describeRouteRef, isOnCourse, routeRefsByPost } from '../map/routeRefs';
import { PostPhotos } from './PostPhotos';
import { PostTagCard } from './PostTags';
import { useUi } from '../state/ui';
import { RowTagChips, TagFilterChips, TagManager, TagPicker } from '../volunteers/TagBits';
import { countUsage, liveFilter, matchesAll, tagSearchText } from '../volunteers/tagLogic';
import { ReadOnlyFieldset } from '../components/ReadOnlyFieldset';
import './posts.css';
import { actorLabel, useCoordinatorLabel } from '../lib/people';

const EQUIP = [
  'brezrokavnik',
  'radijska postaja',
  'znak STOP',
  'svetilka',
  'dežni plašč',
  'prometni stožci',
];

/** Per-type instruction templates. */
const TPL: Record<string, string> = {
  trasa:
    '<p><strong>Naloga:</strong> varno usmerjanje tekačev in ustavljanje vozil ob prehodu.</p><ul><li>Prihod 15 minut pred začetkom izmene.</li><li>Obvezno obleci brezrokavnik.</li><li>Tekače usmerjaj glasno in z roko; vozilo ustavi samo ob prihodu tekača.</li><li>Ne zapiraj ceste trajno — promet spuščaj v vrzelih med tekači.</li></ul><p>Ob težavah ali poškodbi tekača takoj pokliči vodjo sektorja.</p>',
  parkirišče:
    '<p><strong>Naloga:</strong> usmerjanje vozil in avtobusov na parkirne površine.</p><ul><li>Avtobuse usmerjaj na označena mesta ob robu parkirišča.</li><li>Osebna vozila polni od zadaj naprej, brez vrzeli.</li><li>Dovozno pot za intervencijo pusti prosto.</li></ul><p>Ko je parkirišče polno, obvesti vodjo sektorja in usmerjaj na naslednjo lokacijo.</p>',
  'štartno-ciljni':
    '<p><strong>Naloga:</strong> nadzor štartno-ciljnega prostora.</p><ul><li>V ograjeni prostor spuščaj samo tekače s štartno številko in ekipo z akreditacijo.</li><li>Skrbi, da je ciljni koridor 5 m pred ciljem vedno prost.</li><li>Gledalce usmerjaj za ograjo.</li></ul><p>Sodeluj z napovedovalcem in ekipo za merjenje časa.</p>',
};

const typeAccent = (t: string) =>
  t === 'trasa'
    ? 'oklch(0.72 0.07 155)'
    : t === 'parkirišče'
      ? 'oklch(0.74 0.07 245)'
      : 'oklch(0.74 0.08 75)';

const TYPE_PILL: Record<string, { bg: string; fg: string }> = {
  trasa: { bg: 'oklch(0.95 0.035 155)', fg: 'oklch(0.35 0.06 155)' },
  parkirišče: { bg: 'oklch(0.95 0.03 245)', fg: 'oklch(0.36 0.06 245)' },
  'štartno-ciljni': { bg: 'oklch(0.965 0.035 75)', fg: 'oklch(0.38 0.07 75)' },
};

const seg = (on: boolean) =>
  on
    ? { background: 'oklch(0.955 0.025 245)', color: 'oklch(0.34 0.07 245)', border: '1px solid oklch(0.85 0.04 245)' }
    : { background: 'oklch(1 0 0)', color: 'oklch(0.45 0.012 90)', border: '1px solid oklch(0.91 0.006 95)' };

const CARD: React.CSSProperties = {
  background: 'oklch(1 0 0)',
  border: '1px solid oklch(0.92 0.006 95)',
  borderRadius: 11,
};

const LABEL: React.CSSProperties = { fontSize: rem(13), color: 'oklch(0.42 0.012 90)' };

const SMALL_BTN: React.CSSProperties = {
  cursor: 'pointer',
  padding: '5px 8px',
  borderRadius: 6,
  fontSize: rem(12),
  fontFamily: 'inherit',
  background: 'oklch(1 0 0)',
  color: 'oklch(0.45 0.012 90)',
  border: '1px solid oklch(0.91 0.006 95)',
  whiteSpace: 'nowrap',
};

/** "3 izbrana" — the adjective agrees with the implied "mesto". */
function selectedLabel(n: number): string {
  const tail = n % 100;
  const word =
    tail === 1 ? 'izbrano' : tail === 2 ? 'izbrani' : tail === 3 || tail === 4 ? 'izbrana' : 'izbranih';
  return `${n} ${word}`;
}

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '7px 9px',
  borderRadius: 6,
  border: '1px solid oklch(0.9 0.006 95)',
  fontFamily: 'var(--rr-font)',
  fontSize: rem(13.5),
  background: 'oklch(1 0 0)',
  outline: 'none',
};

export function PostsPage() {
  const posts = usePosts();
  const sectors = useSectors();
  const shifts = useAllShifts();
  const event = useEvent();
  const mut = usePostMutations();
  const { selectedDayId } = useUi();
  // Mesta reads for the organisation (SPEC §1.1L): no new post, no labelling.
  const { can } = useSession();
  const canPlan = can('plan:write');

  const tags = useTags('post');
  const tagMut = usePostTagsMutation();

  const [search, setSearch] = useState('');
  // The open post lives in the URL, so Zemljevid can link straight to it.
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('post');
  const setSelectedId = (id: string | null) => setParams(id ? { post: id } : {}, { replace: true });
  // A post opened by link may sit far down a list of ninety; bring its row into view once.
  const linkedId = useRef(selectedId);
  // The list draws once both queries have answered; until then the loader stands in for it.
  const loaded = !posts.isPending && !event.isPending;
  useEffect(() => {
    if (!loaded || !linkedId.current) return;
    document.querySelector(`[data-post-row="${linkedId.current}"]`)?.scrollIntoView({ block: 'center' });
    linkedId.current = null;
  }, [loaded]);
  const [lastSave, setLastSave] = useState<{ postId: string; at: string } | null>(null);
  const [tagFilterIds, setTagFilterIds] = useState<string[]>([]);
  const [tagMgrOpen, setTagMgrOpen] = useState(false);
  // "Označi več": rows pick posts for a bulk label change instead of opening
  // them; the open editor stays as it is.
  const [selectMode, setSelectMode] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const days = event.data?.days ?? [];
  const dayIndex = Math.max(0, days.findIndex((d) => d.id === selectedDayId));
  const tone = dayToneAt(dayIndex);
  const day = days.find((d) => d.id === selectedDayId) ?? null;

  const tagById = useMemo(() => new Map((tags.data ?? []).map((t) => [t.id, t])), [tags.data]);
  // A label deleted while it filters the list must not keep the list empty.
  const tagFilter = useMemo(() => liveFilter(tagFilterIds, tagById), [tagFilterIds, tagById]);
  const tagUsage = useMemo(() => countUsage((posts.data ?? []).map((p) => p.tagIds)), [posts.data]);

  const list = useMemo(() => {
    const rows = posts.data ?? [];
    const q = search.trim().toLowerCase();
    const sectorName = new Map((sectors.data ?? []).map((s) => [s.id, s.name]));
    return rows
      .map((p) => ({ ...p, sectorName: sectorName.get(p.sectorId) ?? '' }))
      .filter(
        (p) =>
          !q ||
          `${p.code} ${p.micro} ${p.sectorName} ${tagSearchText(p.tagIds, tagById)}`
            .toLowerCase()
            .includes(q),
      )
      .filter((p) => matchesAll(p.tagIds, tagFilter));
  }, [posts.data, sectors.data, search, tagById, tagFilter]);

  // From the list itself, so a post deleted in the meantime drops out of the bulk change.
  const bulkTargets = (posts.data ?? []).filter((p) => picked.has(p.id));
  const toggleSelectMode = () => {
    setSelectMode((on) => !on);
    setPicked(new Set());
    setBulkOpen(false);
  };

  const selected = (posts.data ?? []).find((p) => p.id === selectedId) ?? null;

  const newPost = () => {
    const existing = new Set((posts.data ?? []).map((p) => p.code));
    let n = 1;
    while (existing.has(`NOVO${n}`)) n += 1;
    const sectorId = sectors.data?.[0]?.id;
    if (!sectorId) return;
    mut.create.mutate(
      { code: `NOVO${n}`, sectorId, micro: 'nova lokacija', type: 'trasa', urgent: true, status: 'draft' },
      { onSuccess: (created) => setSelectedId(created.id) },
    );
  };

  if (posts.isPending || event.isPending) {
    return (
      <Center mih="60vh">
        <Loader />
      </Center>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: 0, height: 'calc(100vh - 58px)', position: 'relative' }}>
      {tagMgrOpen ? (
        <TagManager
          kind="post"
          tags={tags.data ?? []}
          usage={tagUsage}
          onClose={() => setTagMgrOpen(false)}
        />
      ) : null}
      {/* left rail */}
      <div
        style={{
          width: 306,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'oklch(1 0 0)',
          borderRight: '1px solid oklch(0.9 0.006 95)',
          minHeight: 0,
        }}
      >
        <div
          style={{
            padding: '12px 12px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            borderBottom: '1px solid oklch(0.94 0.005 95)',
          }}
        >
          <input
            style={{ ...INPUT, background: 'oklch(0.985 0.004 95)', padding: '8px 10px', borderRadius: 7 }}
            placeholder="Išči šifro, lokacijo ali oznako…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <TagFilterChips
            tags={tags.data ?? []}
            usage={tagUsage}
            filter={tagFilter}
            onToggle={(id) =>
              setTagFilterIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
            }
            onClear={() => setTagFilterIds([])}
          />
          {canPlan && can('people:write') ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={toggleSelectMode} style={{ ...SMALL_BTN, flex: 1, ...(selectMode ? seg(true) : {}) }}>
                {selectMode ? 'Končaj izbiranje' : 'Označi več'}
              </button>
              <button onClick={() => setTagMgrOpen(true)} style={{ ...SMALL_BTN, flex: 1 }}>
                Uredi oznake
              </button>
            </div>
          ) : null}
          {canPlan ? (
            <button
              onClick={newPost}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                cursor: 'pointer',
                font: 'inherit',
                padding: 8,
                borderRadius: 7,
                border: '1px solid oklch(0.85 0.05 245)',
                background: 'oklch(0.965 0.025 245)',
                color: 'oklch(0.4 0.08 245)',
                fontSize: rem(13.5),
                fontWeight: 600,
              }}
            >
              + Novo redarsko mesto
            </button>
          ) : null}
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
          {list.map((p) => {
            const sel = p.id === selectedId && !selectMode;
            const checked = selectMode && picked.has(p.id);
            const rowTags = (tags.data ?? []).filter((t) => p.tagIds.includes(t.id));
            const pShifts = (shifts.data ?? []).filter((s) => s.postId === p.id);
            const postDayIds = new Set([
              ...p.windows.map((w) => w.eventDayId),
              ...pShifts.map((s) => s.eventDayId),
            ]);
            return (
              <div
                key={p.id}
                data-post-row={p.id}
                onClick={() => {
                  if (!selectMode) return setSelectedId(p.id);
                  const next = new Set(picked);
                  if (checked) next.delete(p.id);
                  else next.add(p.id);
                  setPicked(next);
                }}
                style={{
                  display: 'flex',
                  gap: 9,
                  alignItems: 'flex-start',
                  padding: '9px 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: checked ? 'oklch(0.968 0.022 245)' : sel ? 'oklch(0.965 0.02 245)' : 'oklch(1 0 0)',
                  border: `1px solid ${checked || sel ? 'oklch(0.86 0.04 245)' : 'oklch(0.96 0.005 95)'}`,
                  marginBottom: 4,
                }}
              >
                {selectMode ? (
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      marginTop: 1,
                      flexShrink: 0,
                      borderRadius: 4,
                      background: checked ? 'oklch(0.5 0.09 245)' : 'oklch(1 0 0)',
                      border: `1px solid ${checked ? 'oklch(0.5 0.09 245)' : 'oklch(0.85 0.008 95)'}`,
                      color: 'oklch(1 0 0)',
                      fontSize: rem(11),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {checked ? '✓' : ''}
                  </span>
                ) : null}
                <span
                  style={{
                    width: 3,
                    alignSelf: 'stretch',
                    borderRadius: 2,
                    background: typeAccent(p.type),
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(12.5), fontWeight: 600 }}>
                      {p.code}
                    </span>
                    {p.urgent ? (
                      <span style={{ fontSize: rem(11), fontWeight: 700, letterSpacing: '0.04em', color: 'oklch(0.55 0.12 25)' }}>
                        NUJEN
                      </span>
                    ) : null}
                    {p.status === 'draft' ? (
                      <span style={{ fontSize: rem(11), fontWeight: 700, letterSpacing: '0.04em', color: 'oklch(0.45 0.08 245)' }}>
                        OSNUTEK
                      </span>
                    ) : null}
                    {postDayIds.size > 1 ? (
                      <span title="isto mesto je v uporabi več dni" style={{ display: 'flex', gap: 3 }}>
                        {days
                          .filter((d) => postDayIds.has(d.id))
                          .map((d) => (
                            <span
                              key={d.id}
                              style={{
                                fontFamily: 'var(--rr-font-mono)',
                                fontSize: rem(11),
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                                padding: '1px 5px',
                                borderRadius: 4,
                                background: dayToneAt(days.indexOf(d)).tint,
                                color: dayToneAt(days.indexOf(d)).ink,
                              }}
                            >
                              {d.label.slice(0, 2).toUpperCase()}
                            </span>
                          ))}
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      fontSize: rem(12.5),
                      color: 'oklch(0.48 0.012 90)',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.micro}
                  </div>
                  <div style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)', marginTop: 1 }}>
                    {p.sectorName} · {p.type} · {pShifts.length} izmen
                  </div>
                  <RowTagChips tags={rowTags} />
                </div>
              </div>
            );
          })}
          {list.length === 0 ? (
            <div style={{ padding: '18px 10px', fontSize: rem(13), color: 'oklch(0.6 0.012 90)' }}>
              Ni zadetkov za izbrane filtre.
            </div>
          ) : null}
        </div>

        {selectMode ? (
          <div
            style={{
              flexShrink: 0,
              borderTop: '1px solid oklch(0.92 0.006 95)',
              background: 'oklch(0.988 0.003 95)',
              padding: '9px 10px',
              position: 'relative',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: rem(12.5), fontWeight: 600, color: 'oklch(0.3 0.012 90)' }}>
                {selectedLabel(bulkTargets.length)}
              </span>
              <button
                onClick={() => setPicked(new Set(list.map((p) => p.id)))}
                style={{ cursor: 'pointer', border: 0, background: 'none', padding: 0, fontSize: rem(12), fontFamily: 'inherit', color: 'oklch(0.45 0.07 245)', textDecoration: 'underline' }}
              >
                vse
              </button>
              <button
                onClick={() => setPicked(new Set())}
                style={{ cursor: 'pointer', border: 0, background: 'none', padding: 0, fontSize: rem(12), fontFamily: 'inherit', color: 'oklch(0.5 0.012 90)', textDecoration: 'underline' }}
              >
                nič
              </button>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => bulkTargets.length > 0 && setBulkOpen((o) => !o)}
                style={{
                  cursor: bulkTargets.length > 0 ? 'pointer' : 'default',
                  padding: '6px 11px',
                  borderRadius: 7,
                  fontSize: rem(12.5),
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  border: 0,
                  background: bulkTargets.length > 0 ? 'oklch(0.35 0.05 245)' : 'oklch(0.93 0.006 95)',
                  color: bulkTargets.length > 0 ? 'oklch(1 0 0)' : 'oklch(0.6 0.012 90)',
                }}
              >
                Oznake…
              </button>
            </div>
            {bulkOpen && bulkTargets.length > 0 ? (
              <div style={{ position: 'absolute', left: 10, right: 10, bottom: 46, zIndex: 20 }}>
                <div style={{ fontSize: rem(11), letterSpacing: '0.05em', fontWeight: 600, color: 'oklch(0.55 0.012 90)', marginBottom: 6, background: 'oklch(1 0 0)', borderRadius: 6, padding: '4px 8px', border: '1px solid oklch(0.92 0.006 95)' }}>
                  OZNAKE · {selectedLabel(bulkTargets.length).toUpperCase()}
                </div>
                <TagPicker
                  kind="post"
                  tags={tags.data ?? []}
                  targets={bulkTargets}
                  onApply={(delta) => tagMut.mutate({ postIds: bulkTargets.map((p) => p.id), ...delta })}
                  onClose={() => setBulkOpen(false)}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* editor */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          minWidth: 0,
          background: tone.wash,
          borderLeft: `3px solid ${tone.mid}`,
        }}
      >
        {selected && day ? (
          <PostEditor
            key={selected.id + selected.updatedAt}
            post={selected}
            days={days}
            day={day}
            dayIndex={dayIndex}
            shifts={(shifts.data ?? []).filter((s) => s.postId === selected.id)}
            savedAt={lastSave?.postId === selected.id ? lastSave.at : null}
            onSaved={(at) => setLastSave({ postId: selected.id, at })}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <div style={{ padding: '60px 24px', textAlign: 'center', color: 'oklch(0.55 0.012 90)', fontSize: rem(14), lineHeight: 1.5 }}>
            Izberi redarsko mesto na levi ali ustvari novo.
          </div>
        )}
      </div>
    </div>
  );
}

function PostEditor({
  post,
  days,
  day,
  dayIndex,
  shifts,
  savedAt,
  onSaved,
  onDeleted,
}: {
  post: PostRow;
  days: EventDay[];
  day: EventDay;
  dayIndex: number;
  shifts: Shift[];
  /** When this post was last saved from here — held above the editor, which a save remounts. */
  savedAt: string | null;
  onSaved: (at: string) => void;
  onDeleted: () => void;
}) {
  const navigate = useNavigate();
  const mut = usePostMutations();
  const shiftMut = useShiftMutations();
  const sectors = useSectors();
  const postTags = useTags('post').data ?? [];
  const changelog = useChangelog();
  const { setSelectedDay, selectShift, setAsideTab, selectGap, setChangelogOpen } = useUi();
  const { can } = useSession();
  // Everything this editor writes is the plan (SPEC §1.1L): without it the post is read.
  const canEdit = can('plan:write');
  const tone = dayToneAt(dayIndex);

  const [form, setForm] = useState({
    code: post.code,
    sectorId: post.sectorId,
    micro: post.micro,
    type: post.type,
    urgent: post.urgent,
    gear: post.gear,
    instructions: post.instructions,
    instructionsEn: post.instructionsEn,
    briefingTitle: post.briefingTitle,
    lat: post.lat,
    lng: post.lng,
  });
  /*
   * The English as this editor holds it. Untouched, it follows the server: the
   * translation job writes it without moving `updatedAt`, so the editor is not
   * remounted and must take the new text in rather than save the old one back.
   */
  const [enTouched, setEnTouched] = useState(false);
  /** The English is exactly what "Prevedi" returned, not yet touched by hand. */
  const [enAuto, setEnAuto] = useState(false);
  /** The Slovenian "Prevedi" translated; once it is edited, that translation is stale. */
  const [enFromSl, setEnFromSl] = useState<string | null>(null);
  const [instrLang, setInstrLang] = useState<'sl' | 'en'>('sl');
  const translate = useTranslateInstructions();
  const aiEnabled = useEvent().data?.ai.enabled ?? false;
  const [windowsForm, setWindowsForm] = useState<Record<string, { fromMin: number; toMin: number }[]>>(
    () =>
      Object.fromEntries(
        days.map((d) => [
          d.id,
          post.windows
            .filter((w) => w.eventDayId === d.id)
            .map((w) => ({ fromMin: w.fromMin, toMin: w.toMin }))
            .sort((a, b) => a.fromMin - b.fromMin),
        ]),
      ),
  );
  const [rmDay, setRmDay] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const enEditorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (enTouched) return;
    setForm((f) => (f.instructionsEn === post.instructionsEn ? f : { ...f, instructionsEn: post.instructionsEn }));
    if (enEditorRef.current) enEditorRef.current.innerHTML = post.instructionsEn;
  }, [post.instructionsEn, enTouched]);

  // Each editor is seeded when it mounts — the post remounts this component via
  // its key, and switching tabs mounts the other language's editor afresh.
  useEffect(() => {
    const el = instrLang === 'sl' ? editorRef.current : enEditorRef.current;
    if (el) el.innerHTML = instrLang === 'sl' ? form.instructions : form.instructionsEn;
    // Only on a tab change: the editor owns its markup while it is showing.
  }, [instrLang]);

  // Windows are compared in time order, which is how they come back: the
  // server keeps no order of its own, and one added above another is the same
  // day either way.
  const byStart = (ws: { fromMin: number; toMin: number }[]) =>
    ws.map((w) => ({ fromMin: w.fromMin, toMin: w.toMin })).sort((a, b) => a.fromMin - b.fromMin);
  const origWindows = (dayId: string) => byStart(post.windows.filter((w) => w.eventDayId === dayId));
  const windowsDirty = (dayId: string) =>
    JSON.stringify(byStart(windowsForm[dayId] ?? [])) !== JSON.stringify(origWindows(dayId));

  const enDirty = enTouched && form.instructionsEn !== post.instructionsEn;
  // Text as `postInputSchema` stores it: trimmed.
  const fieldsDirty =
    form.code.trim() !== post.code ||
    form.sectorId !== post.sectorId ||
    form.micro.trim() !== post.micro ||
    form.type !== post.type ||
    form.urgent !== post.urgent ||
    JSON.stringify(form.gear.map((g) => g.trim())) !== JSON.stringify(post.gear) ||
    form.instructions !== post.instructions ||
    enDirty ||
    form.briefingTitle.trim() !== post.briefingTitle ||
    form.lat !== post.lat ||
    form.lng !== post.lng;
  const dirty = fieldsDirty || days.some((d) => windowsDirty(d.id));
  const [saving, setSaving] = useState(false);

  /*
   * Windows first, the post last. Each answer goes into the posts list as it
   * lands, and the post's new `updatedAt` remounts this editor on that list:
   * were a window still on its way, the form would come back holding the old
   * ones and call them unsaved — and the next Shrani would write them back.
   * With the post's answer in place the editor settles when `save` returns,
   * rather than a refetch later with Shrani still open to a 409. "shranjeno" is
   * said only once every write has landed; a failure is toasted by the hook
   * and leaves the form as it was.
   */
  const save = async () => {
    if (saving) return;
    const patch: Record<string, unknown> = {
      id: post.id,
      code: form.code.trim() || post.code,
      sectorId: form.sectorId,
      micro: form.micro.trim() || post.micro,
      type: form.type,
      urgent: form.urgent,
      gear: form.gear,
      instructions: form.instructions,
      location: form.lat !== null && form.lng !== null ? { lat: form.lat, lng: form.lng } : null,
      briefingTitle: form.briefingTitle,
    };
    // A translation of Slovenian edited since is not saved as such: that would
    // stamp it as current. Pressing "Prevedi" asked for the machine's English, so
    // the English is cleared instead — handed back to the job, which translates
    // the new Slovenian, even where the stored English was written by hand.
    const staleTranslation = enAuto && enFromSl !== form.instructions;
    if (enDirty) {
      patch.instructionsEn = staleTranslation ? '' : form.instructionsEn;
      patch.instructionsEnAuto = enAuto;
    }
    // saving an OSNUTEK publishes it
    if (post.status === 'draft') patch.status = 'active';
    setSaving(true);
    try {
      await Promise.all(
        days
          .filter((d) => windowsDirty(d.id))
          .map((d) =>
            mut.setWindows.mutateAsync({
              postId: post.id,
              eventDayId: d.id,
              windows: byStart(windowsForm[d.id] ?? []),
            }),
          ),
      );
      await mut.update.mutateAsync({ ...patch, updatedAt: post.updatedAt } as {
        id: string;
        updatedAt: string;
      });
      const now = new Date();
      onSaved(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`);
    } catch {
      // Already reported by the mutation's own error handler.
    } finally {
      setSaving(false);
    }
  };

  const duplicate = () => {
    mut.create.mutate({
      code: `${post.code}-kopija`,
      sectorId: post.sectorId,
      micro: post.micro,
      type: post.type,
      urgent: post.urgent,
      gear: post.gear,
      instructions: post.instructions,
      location: post.lat !== null && post.lng !== null ? { lat: post.lat, lng: post.lng } : null,
      briefingTitle: post.briefingTitle,
      status: 'draft',
      tagIds: post.tagIds,
    });
  };

  const dayShifts = shifts.filter((s) => s.eventDayId === day.id).sort((a, b) => a.fromMin - b.fromMin);
  const wins = windowsForm[day.id] ?? [];
  const winMin = wins.reduce((a, w) => a + (w.toMin - w.fromMin), 0);
  const covered = mergeCovered(dayShifts.filter(staffs));
  const gaps = subtract(wins, covered).filter((g) => g.toMin - g.fromMin >= 15);
  const gapMin = gaps.reduce((a, g) => a + (g.toMin - g.fromMin), 0);
  const pct = winMin ? Math.round(((winMin - gapMin) / winMin) * 100) : 0;
  const [covBg, covBd, covFg] = winMin ? covTone(pct) : ['oklch(0.985 0.004 95)', 'oklch(0.93 0.006 95)', 'oklch(0.62 0.01 95)'];

  const dirtyLabel = saving
    ? 'shranjujem …'
    : dirty
      ? 'neshranjene spremembe'
      : savedAt
        ? `shranjeno ${savedAt}`
        : 'brez sprememb';

  const labelOf = useCoordinatorLabel();
  const history = (changelog.data?.entries ?? [])
    .filter((e) => (e.entity === 'post' || e.entity === 'post_window') && e.entityId === post.id)
    .slice(0, 6);

  const wordCount = form.instructions
    .replace(/<[^>]*>/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;

  const goShiftInTimeline = (s: Shift) => {
    setSelectedDay(s.eventDayId);
    if (staffs(s)) {
      selectShift(s.id);
      setAsideTab('podrobnosti');
    } else {
      selectGap({ postId: post.id, fromMin: s.fromMin, toMin: s.toMin, shiftId: s.id });
    }
    void navigate('/casovnica');
  };

  const addShift = () => {
    const slot = gaps[0] ?? wins[0] ?? { fromMin: 600, toMin: 780 };
    const toMin = Math.min(slot.toMin, slot.fromMin + 180);
    shiftMut.create.mutate(
      {
        postId: post.id,
        eventDayId: day.id,
        fromMin: slot.fromMin,
        toMin,
        state: 'open',
        note: 'dodano iz urejanja mesta',
      },
      {
        onSuccess: (created) => {
          setSelectedDay(day.id);
          selectShift(created.id);
          setAsideTab('podrobnosti');
          void navigate('/casovnica');
        },
      },
    );
  };

  const syncEditor = () => {
    if (instrLang === 'sl') {
      if (editorRef.current) setForm((f) => ({ ...f, instructions: editorRef.current!.innerHTML }));
    } else if (enEditorRef.current) {
      setEnTouched(true);
      setEnAuto(false);
      setForm((f) => ({ ...f, instructionsEn: enEditorRef.current!.innerHTML }));
    }
  };
  const exec = (cmd: string, arg?: string) => {
    document.execCommand(cmd, false, arg);
    syncEditor();
  };

  return (
    <div style={{ padding: '18px 20px 40px' }}>
      {/* sticky header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 6,
          margin: '-18px -20px 14px',
          padding: '10px 20px 11px',
          background: tone.wash,
          borderBottom: '1px solid oklch(0.9 0.008 95)',
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span
            title={`urejaš podatke za ${dayAcc(day.label)}`}
            style={{
              fontSize: rem(11),
              fontWeight: 700,
              letterSpacing: '0.06em',
              padding: '3px 8px',
              borderRadius: 5,
              background: tone.tint,
              color: tone.ink,
            }}
          >
            {day.label.toUpperCase()}
          </span>
          <span style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(19), fontWeight: 700, letterSpacing: '-0.01em' }}>
            {form.code}
          </span>
          <span
            style={{
              fontSize: rem(12),
              padding: '2px 8px',
              borderRadius: 20,
              fontWeight: 500,
              background: TYPE_PILL[form.type]?.bg,
              color: TYPE_PILL[form.type]?.fg,
            }}
          >
            {form.type}
          </span>
          {post.status === 'draft' ? (
            <span style={{ fontSize: rem(11), fontWeight: 700, letterSpacing: '0.04em', color: 'oklch(0.45 0.08 245)' }}>
              OSNUTEK
            </span>
          ) : null}
          <div style={{ flex: 1 }} />
          {canEdit ? (
            <>
              <button
                onClick={duplicate}
                style={{
                  cursor: 'pointer',
                  font: 'inherit',
                  padding: '7px 11px',
                  borderRadius: 7,
                  border: '1px solid oklch(0.89 0.008 95)',
                  background: 'oklch(1 0 0)',
                  fontSize: rem(13),
                  fontWeight: 500,
                  color: 'oklch(0.35 0.012 90)',
                }}
              >
                Podvoji
              </button>
              <button
                onClick={() => mut.remove.mutate(post.id, { onSuccess: onDeleted })}
                style={{
                  cursor: 'pointer',
                  font: 'inherit',
                  padding: '7px 11px',
                  borderRadius: 7,
                  border: '1px solid oklch(0.9 0.03 25)',
                  background: 'oklch(1 0 0)',
                  fontSize: rem(13),
                  fontWeight: 500,
                  color: 'oklch(0.5 0.12 25)',
                }}
              >
                Izbriši
              </button>
              <button
                onClick={() => void save()}
                disabled={saving}
                style={{
                  cursor: saving ? 'default' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  font: 'inherit',
                  padding: '7px 15px',
                  borderRadius: 7,
                  border: '1px solid oklch(0.55 0.09 155)',
                  background: 'oklch(0.62 0.09 155)',
                  fontSize: rem(13),
                  fontWeight: 600,
                  color: 'oklch(1 0 0)',
                }}
              >
                Shrani
              </button>
            </>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              fontSize: rem(13.5),
              color: 'oklch(0.45 0.012 90)',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {(sectors.data ?? []).find((s) => s.id === form.sectorId)?.name ?? ''} · {form.micro}
          </span>
          <span style={{ fontSize: rem(12), color: dirty ? 'oklch(0.55 0.1 75)' : 'oklch(0.62 0.012 90)' }}>
            {canEdit ? dirtyLabel : null}
          </span>
        </div>
      </div>

      {/* DNEVI MESTA */}
      <div style={{ ...CARD, padding: '12px 14px 13px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: rem(11), letterSpacing: '0.05em', fontWeight: 600, color: 'oklch(0.5 0.012 90)' }}>
            DNEVI MESTA
          </span>
          <span style={{ fontSize: rem(12.5), color: 'oklch(0.55 0.012 90)', lineHeight: 1.35 }}>
            Opis, navodila in oprema so skupni vsem dnevom — okna in izmene vodiš ločeno po dnevih.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {days.map((d, i) => {
            const dw = windowsForm[d.id] ?? [];
            const dShifts = shifts.filter((s) => s.eventDayId === d.id);
            const active = dw.length > 0 || dShifts.length > 0;
            const dTone = dayToneAt(i);
            const isCurrent = d.id === day.id;
            if (!active) {
              // Putting the post on another day is a plan write.
              if (!canEdit) return null;
              return (
                <button
                  key={d.id}
                  onClick={() =>
                    setWindowsForm((prev) => ({
                      ...prev,
                      [d.id]: wins.length ? wins.map((w) => ({ ...w })) : [{ fromMin: 360, toMin: 1200 }],
                    }))
                  }
                  style={{
                    cursor: 'pointer',
                    font: 'inherit',
                    textAlign: 'left',
                    flex: '1 1 210px',
                    minWidth: 200,
                    border: '1px dashed oklch(0.86 0.03 245)',
                    background: 'oklch(0.99 0.006 245)',
                    borderRadius: 9,
                    padding: '9px 11px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <span style={{ fontSize: rem(13.5), fontWeight: 600, color: 'oklch(0.4 0.08 245)' }}>
                    + Dodaj na {dayAcc(d.label)}
                  </span>
                  <span style={{ fontSize: rem(11.5), color: 'oklch(0.55 0.03 245)', lineHeight: 1.35 }}>
                    ista lokacija, navodila in oprema — izmene ostanejo prazne
                  </span>
                </button>
              );
            }
            const dwMin = dw.reduce((a, w) => a + (w.toMin - w.fromMin), 0);
            const dCovered = mergeCovered(dShifts.filter(staffs));
            const dGapMin = subtract(dw, dCovered)
              .filter((g) => g.toMin - g.fromMin >= 15)
              .reduce((a, g) => a + (g.toMin - g.fromMin), 0);
            const dPct = dwMin ? Math.round(((dwMin - dGapMin) / dwMin) * 100) : 0;
            return (
              <div
                key={d.id}
                onClick={() => setSelectedDay(d.id)}
                style={{
                  cursor: 'pointer',
                  flex: '1 1 210px',
                  minWidth: 200,
                  border: `1px solid ${isCurrent ? dTone.bd : 'oklch(0.93 0.006 95)'}`,
                  borderLeft: `3px solid ${dTone.mid}`,
                  background: isCurrent ? dTone.tint : 'oklch(1 0 0)',
                  borderRadius: 9,
                  padding: '9px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: rem(13.5), fontWeight: 600, color: dTone.ink }}>{d.label}</span>
                    {isCurrent ? (
                      <span style={{ fontSize: rem(11), fontWeight: 700, letterSpacing: '0.04em', color: dTone.ink, opacity: 0.75 }}>
                        UREJAŠ
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(11.5), color: 'oklch(0.5 0.012 90)' }}>
                    {dw.length ? dw.map((w) => `${m2t(w.fromMin)}–${m2t(w.toMin)}`).join(' + ') : 'okno ni določeno'}
                  </div>
                  <div style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)' }}>
                    {dShifts.length ? plS(dShifts.length) : 'brez izmen'}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: 'var(--rr-font-mono)',
                    fontSize: rem(13.5),
                    fontWeight: 700,
                    padding: '4px 8px',
                    borderRadius: 6,
                    background: dwMin ? covTone(dPct)[0] : 'oklch(0.97 0.005 95)',
                    color: dwMin ? covTone(dPct)[2] : 'oklch(0.62 0.01 95)',
                  }}
                >
                  {dwMin ? `${dPct} %` : '—'}
                </span>
                {canEdit ? (
                  <button
                    title="odstrani mesto s tega dneva"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (dShifts.length) setRmDay(d.id);
                      else setWindowsForm((prev) => ({ ...prev, [d.id]: [] }));
                    }}
                    style={{
                      cursor: 'pointer',
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      border: '1px solid oklch(0.93 0.006 95)',
                      background: 'oklch(1 0 0)',
                      color: 'oklch(0.58 0.012 90)',
                      fontSize: rem(12),
                      flexShrink: 0,
                    }}
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
        {rmDay ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              padding: '9px 11px',
              borderRadius: 9,
              background: 'oklch(0.98 0.02 25)',
              border: '1px solid oklch(0.89 0.05 25)',
            }}
          >
            <span style={{ fontSize: rem(12.5), color: 'oklch(0.44 0.12 25)', flex: 1, minWidth: 0 }}>
              Odstranim {post.code} {dayFrom(days.find((d) => d.id === rmDay)?.label ?? '')}? Okno bo
              izbrisano — izmene tega dne uredi na časovnici.
            </span>
            <button
              onClick={() => setRmDay(null)}
              style={{
                cursor: 'pointer',
                font: 'inherit',
                padding: '6px 11px',
                borderRadius: 7,
                border: '1px solid oklch(0.91 0.006 95)',
                background: 'oklch(1 0 0)',
                fontSize: rem(12.5),
                color: 'oklch(0.4 0.012 90)',
              }}
            >
              Prekliči
            </button>
            <button
              onClick={() => {
                setWindowsForm((prev) => ({ ...prev, [rmDay]: [] }));
                setRmDay(null);
              }}
              style={{
                cursor: 'pointer',
                font: 'inherit',
                padding: '6px 12px',
                borderRadius: 7,
                border: '1px solid oklch(0.82 0.09 25)',
                background: 'oklch(0.62 0.14 25)',
                fontSize: rem(12.5),
                fontWeight: 600,
                color: 'oklch(1 0 0)',
              }}
            >
              Odstrani
            </button>
          </div>
        ) : null}
      </div>

      {/* POKRITOST MESTA */}
      <div style={{ ...CARD, border: `1px solid ${covBd}`, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex' }}>
          <div
            style={{
              width: 172,
              flexShrink: 0,
              padding: '14px 16px',
              background: covBg,
              borderRight: `1px solid ${covBd}`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: rem(11), letterSpacing: '0.05em', fontWeight: 600, color: 'oklch(0.5 0.012 90)' }}>
                POKRITOST MESTA
              </span>
              <span
                style={{
                  fontSize: rem(11),
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  padding: '1px 5px',
                  borderRadius: 4,
                  background: tone.tint,
                  color: tone.ink,
                }}
              >
                {day.label.toUpperCase()}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span
                style={{
                  fontFamily: 'var(--rr-font-mono)',
                  fontSize: rem(31),
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: covFg,
                }}
              >
                {winMin ? `${pct}%` : '—'}
              </span>
              <span style={{ fontSize: rem(13), color: covFg }}>{winMin ? 'pokrito' : 'brez okna'}</span>
            </div>
            <span style={{ fontSize: rem(12), color: 'oklch(0.5 0.012 90)', lineHeight: 1.35 }}>
              {wins.length
                ? `${wins.map((w) => `${m2t(w.fromMin)}–${m2t(w.toMin)}`).join(' + ')} · ${dur(winMin)}${gapMin ? ` · vrzel ${dur(gapMin)}` : ''}`
                : 'okno mesta ni določeno'}
            </span>
          </div>
          <div style={{ flex: 1, padding: '13px 16px 14px', display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              {(
                [
                  [dayShifts.filter(staffs).length, 'zasedenih', 'oklch(0.62 0.09 155)'],
                  [gaps.length, 'vrzeli', 'oklch(0.6 0.13 25)'],
                  [dayShifts.filter((s) => !staffs(s)).length, 'nezasedenih', 'oklch(0.72 0.1 75)'],
                ] as const
              ).map(([v, lbl, dot]) => (
                <span key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot }} />
                  <span style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(14), fontWeight: 600 }}>{v}</span>
                  <span style={{ fontSize: rem(12), color: 'oklch(0.52 0.012 90)' }}>{lbl}</span>
                </span>
              ))}
              <div style={{ flex: 1 }} />
              {canEdit ? (
                <button
                  onClick={addShift}
                  style={{
                    cursor: 'pointer',
                    font: 'inherit',
                    padding: '5px 11px',
                    borderRadius: 6,
                    border: '1px dashed oklch(0.84 0.008 95)',
                    background: 'oklch(0.99 0.004 95)',
                    fontSize: rem(12.5),
                    color: 'oklch(0.4 0.012 90)',
                  }}
                >
                  + Dodaj izmeno
                </button>
              ) : null}
            </div>
            {dayShifts.length ? (
              dayShifts.map((s) => (
                <button
                  key={s.id}
                  onClick={() => goShiftInTimeline(s)}
                  style={{
                    textAlign: 'left',
                    font: 'inherit',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '7px 10px',
                    borderRadius: 7,
                    border: '1px solid oklch(0.94 0.006 95)',
                    background: 'oklch(1 0 0)',
                  }}
                >
                  <span style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(13), fontWeight: 600, width: 96 }}>
                    {m2t(s.fromMin)}–{m2t(s.toMin)}
                  </span>
                  <span style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(11.5), color: 'oklch(0.58 0.012 90)', width: 76 }}>
                    {dur(s.toMin - s.fromMin)}
                  </span>
                  <ShiftPerson shift={s} />
                  <span style={{ fontSize: rem(12), color: 'oklch(0.7 0.012 90)' }}>↗</span>
                </button>
              ))
            ) : (
              <div
                style={{
                  borderRadius: 5,
                  border: '1px dashed oklch(0.88 0.008 95)',
                  padding: '12px 0',
                  textAlign: 'center',
                  fontSize: rem(12.5),
                  color: 'oklch(0.6 0.012 90)',
                }}
              >
                Za ta dan še ni izmen na tem mestu.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* two-column form grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <PostTagCard post={post} tags={postTags} />

          <ReadOnlyFieldset readOnly={!canEdit}>
            {/* OSNOVNI PODATKI */}
            <div style={{ ...CARD, padding: 16 }}>
              <div style={{ fontSize: rem(12), letterSpacing: '0.04em', color: 'oklch(0.55 0.012 90)', marginBottom: 12 }}>
                OSNOVNI PODATKI
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '132px minmax(0, 1fr)', gap: '10px 12px', alignItems: 'center' }}>
                <label style={LABEL}>Šifra</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    disabled={post.status !== 'draft'}
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.currentTarget.value })}
                    style={{
                      ...INPUT,
                      width: 120,
                      fontFamily: 'var(--rr-font-mono)',
                      fontSize: rem(13),
                      background: post.status === 'draft' ? 'oklch(1 0 0)' : 'oklch(0.965 0.005 95)',
                    }}
                  />
                  <span style={{ fontSize: rem(12), color: 'oklch(0.6 0.012 90)' }}>
                    {post.status === 'draft'
                      ? 'shranjevanje objavi osnutek'
                      : 'šifre obstoječega mesta ni mogoče spremeniti'}
                  </span>
                </div>

                <label style={LABEL}>Makro lokacija</label>
                <select
                  value={form.sectorId}
                  onChange={(e) => setForm({ ...form, sectorId: e.currentTarget.value })}
                  style={INPUT}
                >
                  {(sectors.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>

                <label style={LABEL}>Mikro lokacija</label>
                <input
                  value={form.micro}
                  placeholder="npr. Petacov most – desni breg"
                  onChange={(e) => setForm({ ...form, micro: e.currentTarget.value })}
                  style={INPUT}
                />

                <label style={LABEL}>Naziv za brifing</label>
                <input
                  value={form.briefingTitle}
                  placeholder={
                    briefingPostHeading({
                      micro: form.micro,
                      sector: (sectors.data ?? []).find((s) => s.id === form.sectorId)?.name ?? '',
                    }).title || 'naslov kartice na brifingu'
                  }
                  title="Prazno: naslov se sestavi iz mikro lokacije."
                  onChange={(e) => setForm({ ...form, briefingTitle: e.currentTarget.value })}
                  style={INPUT}
                />

                <label style={{ ...LABEL, alignSelf: 'start', paddingTop: 7, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>Časovnica mesta</span>
                  <span
                    style={{
                      fontSize: rem(11),
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      padding: '1px 5px',
                      borderRadius: 4,
                      background: tone.tint,
                      color: tone.ink,
                    }}
                  >
                    {day.label.toUpperCase()}
                  </span>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {wins.map((w, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <TimeField
                        title="Odprtje mesta"
                        value={w.fromMin}
                        onChange={(min) =>
                          setWindowsForm((prev) => ({
                            ...prev,
                            [day.id]: wins.map((x, j) => (j === i ? { ...x, fromMin: min } : x)),
                          }))
                        }
                      />
                      <span style={{ fontSize: rem(13), color: 'oklch(0.6 0.012 90)' }}>–</span>
                      <TimeField
                        title="Zaprtje mesta"
                        value={Math.min(w.toMin, 1439)}
                        onChange={(min) =>
                          setWindowsForm((prev) => ({
                            ...prev,
                            [day.id]: wins.map((x, j) => (j === i ? { ...x, toMin: min } : x)),
                          }))
                        }
                      />
                      {wins.length > 1 && canEdit ? (
                        <button
                          title="odstrani interval"
                          onClick={() =>
                            setWindowsForm((prev) => ({ ...prev, [day.id]: wins.filter((_, j) => j !== i) }))
                          }
                          style={{
                            cursor: 'pointer',
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            border: '1px solid oklch(0.91 0.006 95)',
                            background: 'oklch(1 0 0)',
                            color: 'oklch(0.5 0.012 90)',
                            fontSize: rem(12),
                          }}
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {/* Editing the window; the times above already say what it is. */}
                  {canEdit ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        const last = wins.at(-1);
                        const fromMin = Math.min(last ? last.toMin + 60 : 360, 1380);
                        setWindowsForm((prev) => ({
                          ...prev,
                          [day.id]: [...wins, { fromMin, toMin: Math.min(fromMin + 180, 1440) }],
                        }));
                      }}
                      style={{
                        cursor: 'pointer',
                        font: 'inherit',
                        padding: '5px 10px',
                        borderRadius: 6,
                        border: '1px dashed oklch(0.85 0.008 95)',
                        background: 'oklch(0.985 0.004 95)',
                        fontSize: rem(12.5),
                        color: 'oklch(0.4 0.012 90)',
                      }}
                    >
                      + Dodaj interval
                    </button>
                    {dayShifts.length ? (
                      <button
                        title="nastavi okno na obseg obstoječih izmen"
                        onClick={() =>
                          setWindowsForm((prev) => ({
                            ...prev,
                            [day.id]: [dayExtent(dayShifts)!],
                          }))
                        }
                        style={{
                          cursor: 'pointer',
                          font: 'inherit',
                          padding: '5px 10px',
                          borderRadius: 6,
                          border: '1px solid oklch(0.92 0.006 95)',
                          background: 'oklch(1 0 0)',
                          fontSize: rem(12.5),
                          color: 'oklch(0.48 0.012 90)',
                        }}
                      >
                        Po izmenah
                      </button>
                    ) : null}
                    <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)', lineHeight: 1.3 }}>
                      izmene ne morejo ven iz okna
                    </span>
                  </div>
                  ) : null}
                </div>

                <label style={LABEL}>Tip mesta</label>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {(['trasa', 'parkirišče', 'štartno-ciljni'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => setForm({ ...form, type: t })}
                      style={{
                        cursor: 'pointer',
                        font: 'inherit',
                        padding: '6px 11px',
                        borderRadius: 6,
                        fontSize: rem(13),
                        fontWeight: 500,
                        ...seg(form.type === t),
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                <label style={LABEL}>Stopnja nujnosti</label>
                <div style={{ display: 'flex', gap: 5 }}>
                  {(
                    [
                      ['nujen', true],
                      ['zaželjen', false],
                    ] as const
                  ).map(([lbl, val]) => (
                    <button
                      key={lbl}
                      onClick={() => setForm({ ...form, urgent: val })}
                      style={{
                        cursor: 'pointer',
                        font: 'inherit',
                        padding: '6px 11px',
                        borderRadius: 6,
                        fontSize: rem(13),
                        fontWeight: 500,
                        ...seg(form.urgent === val),
                      }}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>

                <label style={{ ...LABEL, alignSelf: 'start', paddingTop: 5 }}>Oprema</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {/* Read-only shows what the post has, not the catalogue to pick from. */}
                  {(canEdit ? [...new Set([...EQUIP, ...form.gear])] : form.gear).map((g) => {
                    const on = form.gear.includes(g);
                    return (
                      <button
                        key={g}
                        onClick={() =>
                          setForm({
                            ...form,
                            gear: on ? form.gear.filter((x) => x !== g) : [...form.gear, g],
                          })
                        }
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          cursor: 'pointer',
                          font: 'inherit',
                          padding: '5px 10px',
                          borderRadius: 20,
                          fontSize: rem(12.5),
                          ...seg(on),
                        }}
                      >
                        <span style={{ fontSize: rem(11) }}>{on ? '✓' : '+'}</span>
                        <span>{g}</span>
                      </button>
                    );
                  })}
                </div>

              </div>
            </div>

          </ReadOnlyFieldset>

          {/* NAVODILA ZA REDARJA */}
          <div style={{ ...CARD, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '13px 16px 11px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: rem(12), letterSpacing: '0.04em', color: 'oklch(0.55 0.012 90)' }}>
                NAVODILA ZA REDARJA
              </span>
              <span style={{ fontSize: rem(12), color: 'oklch(0.62 0.012 90)' }}>
                — vsebina gre v info mail in brifing
              </span>
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 3 }} role="tablist" aria-label="Jezik navodil">
                {(
                  [
                    ['sl', 'Slovenščina'],
                    ['en', 'English'],
                  ] as const
                ).map(([code, label]) => (
                  <button
                    key={code}
                    role="tab"
                    aria-selected={instrLang === code}
                    onClick={() => setInstrLang(code)}
                    style={{
                      cursor: 'pointer',
                      font: 'inherit',
                      padding: '5px 10px',
                      borderRadius: 6,
                      fontSize: rem(12.5),
                      fontWeight: 500,
                      ...seg(instrLang === code),
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {canEdit && instrLang === 'sl' ? (
                <button
                  onClick={() => {
                    const tpl = TPL[form.type] ?? TPL.trasa!;
                    if (editorRef.current) editorRef.current.innerHTML = tpl;
                    setForm((f) => ({ ...f, instructions: tpl }));
                  }}
                  style={{
                    cursor: 'pointer',
                    font: 'inherit',
                    padding: '5px 10px',
                    borderRadius: 6,
                    border: '1px solid oklch(0.9 0.006 95)',
                    background: 'oklch(0.985 0.004 95)',
                    fontSize: rem(12.5),
                    color: 'oklch(0.4 0.012 90)',
                  }}
                >
                  Vstavi predlogo za {form.type}
                </button>
              ) : null}
            </div>
            {instrLang === 'en' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px 10px', flexWrap: 'wrap' }}>
                <EnglishBadge
                  post={post}
                  dirty={enDirty}
                  auto={enAuto}
                  stale={enAuto && enFromSl !== form.instructions}
                />
                <div style={{ flex: 1 }} />
                {canEdit && aiEnabled ? (
                  <button
                    disabled={translate.isPending || form.instructions.trim() === ''}
                    onClick={() =>
                      translate.mutate(
                        { postId: post.id, instructions: form.instructions },
                        {
                          onSuccess: ({ html }) => {
                            if (enEditorRef.current) enEditorRef.current.innerHTML = html;
                            setForm((f) => ({ ...f, instructionsEn: html }));
                            setEnTouched(true);
                            setEnAuto(true);
                            setEnFromSl(form.instructions);
                          },
                        },
                      )
                    }
                    style={{ ...SMALL_BTN, opacity: translate.isPending ? 0.6 : 1 }}
                  >
                    {translate.isPending ? 'prevajam …' : 'Prevedi iz slovenščine'}
                  </button>
                ) : null}
              </div>
            ) : null}
            {canEdit ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  padding: '0 12px 10px',
                  borderBottom: '1px solid oklch(0.94 0.005 95)',
                  flexWrap: 'wrap',
                }}
              >
                {(
                  [
                    ['B', 'bold', undefined, 'Polkrepko', { fontWeight: 700 }],
                    ['I', 'italic', undefined, 'Ležeče', { fontStyle: 'italic' }],
                    ['U', 'underline', undefined, 'Podčrtano', { textDecoration: 'underline' }],
                    ['H', 'formatBlock', '<h3>', 'Naslov', { fontWeight: 700, fontSize: rem(14) }],
                    ['¶', 'formatBlock', '<p>', 'Odstavek', {}],
                    ['•', 'insertUnorderedList', undefined, 'Označen seznam', { fontSize: rem(15) }],
                    ['1.', 'insertOrderedList', undefined, 'Številčen seznam', {}],
                    ['↺', 'undo', undefined, 'Razveljavi', {}],
                    ['⌫', 'removeFormat', undefined, 'Odstrani oblikovanje', {}],
                  ] as const
                ).map(([lbl, cmd, arg, title, extra]) => (
                  <button
                    key={lbl}
                    title={title}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      exec(cmd, arg);
                    }}
                    style={{
                      minWidth: 30,
                      height: 28,
                      padding: '0 8px',
                      borderRadius: 6,
                      border: '1px solid oklch(0.93 0.006 95)',
                      background: 'oklch(1 0 0)',
                      color: 'oklch(0.3 0.012 90)',
                      cursor: 'pointer',
                      fontSize: rem(13),
                      fontWeight: 500,
                      ...(extra as React.CSSProperties),
                    }}
                  >
                    {lbl}
                  </button>
                ))}
                <span style={{ width: 1, height: 20, background: 'oklch(0.93 0.006 95)', margin: '0 5px' }} />
                <span style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>
                  {wordCount} besed
                </span>
              </div>
            ) : null}
            <div
              key={instrLang}
              ref={instrLang === 'sl' ? editorRef : enEditorRef}
              lang={instrLang}
              contentEditable={canEdit}
              suppressContentEditableWarning
              onInput={syncEditor}
              style={{
                padding: '14px 16px 20px',
                minHeight: 190,
                fontSize: rem(14.5),
                lineHeight: 1.55,
                color: 'oklch(0.28 0.012 90)',
                outline: 'none',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <ReadOnlyFieldset readOnly={!canEdit}>
            {/* MIKRO LOKACIJA */}
            <div style={{ ...CARD, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                <span style={{ fontSize: rem(12), letterSpacing: '0.04em', color: 'oklch(0.55 0.012 90)' }}>
                  MIKRO LOKACIJA
                </span>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: rem(12), color: 'oklch(0.62 0.012 90)' }}>
                  koordinate se rišejo na zemljevidu in v brifingu
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(
                  [
                    ['LAT', form.lat, (v: number | null) => setForm({ ...form, lat: v })],
                    ['LON', form.lng, (v: number | null) => setForm({ ...form, lng: v })],
                  ] as const
                ).map(([lbl, val, set]) => (
                  <div
                    key={lbl}
                    style={{
                      flex: 1,
                      padding: '7px 9px',
                      borderRadius: 6,
                      background: 'oklch(0.978 0.004 95)',
                      border: '1px solid oklch(0.93 0.006 95)',
                    }}
                  >
                    <div style={{ fontSize: rem(11), letterSpacing: '0.04em', color: 'oklch(0.58 0.012 90)' }}>{lbl}</div>
                    <input
                      value={val ?? ''}
                      placeholder="—"
                      onChange={(e) => {
                        const raw = e.currentTarget.value.trim().replace(',', '.');
                        const num = raw === '' ? null : Number(raw);
                        set(num !== null && Number.isFinite(num) ? num : null);
                      }}
                      style={{
                        width: '100%',
                        border: 0,
                        outline: 'none',
                        background: 'transparent',
                        fontFamily: 'var(--rr-font-mono)',
                        fontSize: rem(13),
                        fontWeight: 500,
                      }}
                    />
                  </div>
                ))}
              </div>
              <RouteRefLine lat={form.lat} lng={form.lng} />
              <PostMapPreview lat={form.lat} lng={form.lng} code={form.code} color={typeAccent(form.type)} />
            </div>

          </ReadOnlyFieldset>

          {/* FOTOGRAFIJE MESTA — next to the coordinates, because a photo of the
              post is location information too. */}
          <PostPhotos post={post} />

          {/* ZGODOVINA MESTA */}
          {can('audit:read') ? (
            <div style={{ ...CARD, padding: '14px 8px 10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: rem(12), letterSpacing: '0.04em', color: 'oklch(0.55 0.012 90)' }}>
                  ZGODOVINA MESTA
                </span>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => setChangelogOpen(true)}
                  style={{
                    border: 0,
                    background: 'none',
                    cursor: 'pointer',
                    font: 'inherit',
                    fontSize: rem(12),
                    color: 'oklch(0.5 0.09 245)',
                    padding: 0,
                  }}
                >
                  celoten dnevnik ↗
                </button>
              </div>
              {history.length ? (
                history.map((e) => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px 5px 8px' }}>
                    <span style={{ fontFamily: 'var(--rr-font-mono)', fontSize: rem(11), color: 'oklch(0.6 0.012 90)', width: 74, flexShrink: 0 }}>
                      {new Date(e.createdAt).toLocaleDateString('sl-SI', { day: 'numeric', month: 'numeric' })}{' '}
                      {new Date(e.createdAt).toLocaleTimeString('sl-SI', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: rem(12.5), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 600, color: 'oklch(0.42 0.012 90)' }}>{actorLabel(e, labelOf)}</span>
                      <span style={{ color: 'oklch(0.78 0.008 95)' }}> · </span>
                      <span style={{ fontWeight: 600, color: e.breaking ? 'oklch(0.47 0.14 25)' : 'oklch(0.24 0.012 90)' }}>
                        {entryTitle(e)}
                      </span>
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)', padding: '4px 0 6px' }}>
                  Za to mesto še ni zabeleženih sprememb.
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const HOME: [number, number] = [46.43, 13.95];

/** Keeps the preview centered as coordinates are typed. */
function Recenter({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap();
  useEffect(() => {
    const center: [number, number] = lat !== null && lng !== null ? [lat, lng] : HOME;
    map.setView(center, lat !== null && lng !== null ? 15 : 11, { animate: false });
  }, [lat, lng, map]);
  return null;
}

/**
 * Where this post sits along the race course, derived from the imported GPX
 * tracks. Doubles as a check on the coordinates typed above: a post that is
 * nowhere near a course is almost always a mistyped lat/lng.
 */
function RouteRefLine({ lat, lng }: { lat: number | null; lng: number | null }) {
  const routes = useRaceRoutes();
  const ref = useMemo(() => {
    if (lat === null || lng === null) return null;
    return routeRefsByPost([{ id: 'form', lat, lng }], routes.data ?? []).get('form') ?? null;
  }, [lat, lng, routes.data]);

  if (!ref) return null;
  const onCourse = isOnCourse(ref);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        marginTop: 8,
        padding: '6px 9px',
        borderRadius: 6,
        background: onCourse ? 'oklch(0.978 0.004 95)' : 'oklch(0.99 0.015 25)',
        border: `1px solid ${onCourse ? 'oklch(0.93 0.006 95)' : 'oklch(0.92 0.04 25)'}`,
        fontSize: rem(12.5),
        color: onCourse ? 'oklch(0.42 0.012 90)' : 'oklch(0.48 0.13 25)',
      }}
    >
      <span
        style={{
          width: 14,
          height: 3,
          borderRadius: 2,
          background: onCourse ? ref.routeColor : 'oklch(0.6 0.15 25)',
          flexShrink: 0,
        }}
      />
      <span>{describeRouteRef(ref)}</span>
    </div>
  );
}

/** Static preview map with a single pin for the post's micro-location. */
function PostMapPreview({ lat, lng, code, color }: { lat: number | null; lng: number | null; code: string; color: string }) {
  const pos: [number, number] | null = lat !== null && lng !== null ? [lat, lng] : null;
  const icon = useMemo(
    () =>
      L.divIcon({
        className: 'pp-pin',
        iconSize: [26, 26],
        iconAnchor: [13, 26],
        html: `<div class="pp-pbody" style="background:${color}"><span>${code.charAt(0)}</span></div>`,
      }),
    [code, color],
  );
  return (
    <div className="pp-minimap">
      <MapContainer
        center={pos ?? HOME}
        zoom={pos ? 15 : 11}
        style={{ height: '100%' }}
        dragging={false}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {pos ? <Marker position={pos} icon={icon} /> : null}
        <Recenter lat={lat} lng={lng} />
      </MapContainer>
      {pos ? (
        <a
          className="pp-open"
          title="Odpri v zemljevidu"
          href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`}
          target="_blank"
          rel="noreferrer"
        >
          ↗
        </a>
      ) : (
        <div className="pp-hint">koordinate še niso vnešene</div>
      )}
    </div>
  );
}

function ShiftPerson({ shift: s }: { shift: Shift }) {
  const free = !staffs(s);
  return (
    <span
      style={{
        fontSize: rem(13.5),
        fontWeight: 500,
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: free ? 'oklch(0.5 0.1 25)' : 'oklch(0.24 0.012 90)',
      }}
    >
      {free ? 'Nezasedeno' : <ShiftVolunteerName volunteerId={s.volunteerId!} />}
      {s.state === 'proposed' ? ' · PREDLOG' : s.state === 'done' ? ' · opravljeno' : ''}
    </span>
  );
}

function ShiftVolunteerName({ volunteerId }: { volunteerId: string }) {
  const volunteers = useVolunteers();
  // `GET /volunteers` is the roster only, so a shift covered by an external team
  // is not in it (SPEC §1.1J) and would render '?' — a post reading as staffed
  // by nobody. Their names come from the teams list instead.
  const groups = useExternalGroups();
  const name =
    (volunteers.data ?? []).find((v) => v.id === volunteerId)?.name ??
    (groups.data ?? []).flatMap((g) => g.slots).find((s) => s.id === volunteerId)?.name;
  return <>{name ?? '?'}</>;
}

/** Merge staffed shift intervals into a coverage set. */
function mergeCovered(shifts: { fromMin: number; toMin: number }[]) {
  const sorted = [...shifts].sort((a, b) => a.fromMin - b.fromMin);
  const out: { fromMin: number; toMin: number }[] = [];
  for (const s of sorted) {
    const last = out.at(-1);
    if (last && s.fromMin <= last.toMin) last.toMin = Math.max(last.toMin, s.toMin);
    else out.push({ fromMin: s.fromMin, toMin: s.toMin });
  }
  return out;
}

/** Parts of `windows` not covered by `covered`. */
function subtract(
  windows: { fromMin: number; toMin: number }[],
  covered: { fromMin: number; toMin: number }[],
) {
  const gaps: { fromMin: number; toMin: number }[] = [];
  for (const w of windows) {
    let cursor = w.fromMin;
    for (const c of covered) {
      if (c.toMin <= cursor || c.fromMin >= w.toMin) continue;
      if (c.fromMin > cursor) gaps.push({ fromMin: cursor, toMin: c.fromMin });
      cursor = Math.max(cursor, c.toMin);
    }
    if (cursor < w.toMin) gaps.push({ fromMin: cursor, toMin: w.toMin });
  }
  return gaps;
}

/**
 * Where the English instructions stand. The briefing shows English only while it
 * was made from the Slovenian as it stands; anything else here says why an
 * English reader would see Slovenian instead.
 */
function EnglishBadge({ post, dirty, auto, stale }: { post: PostRow; dirty: boolean; auto: boolean; stale: boolean }) {
  const [text, tone] = dirty
    ? stale
      ? ['slovenščina se je spremenila po prevodu — ob shranjevanju se prevede znova', 'oklch(0.5 0.12 60)']
      : auto
      ? ['samodejni prevod — ni še shranjen', 'oklch(0.45 0.09 245)']
      : ['ročno urejeno — ni še shranjeno', 'oklch(0.45 0.09 245)']
    : !post.instructionsEn
      ? [post.instructions.trim() ? 'ni prevoda — brifing kaže slovenščino' : 'ni navodil', 'oklch(0.5 0.012 90)']
      : !post.instructionsEnFresh
        ? post.instructionsEnManual
          ? ['angleščina je morda zastarela — slovenščina se je spremenila', 'oklch(0.5 0.12 60)']
          : ['prevod v pripravi — brifing do takrat kaže slovenščino', 'oklch(0.5 0.12 60)']
        : post.instructionsEnManual
          ? ['ročno urejeno', 'oklch(0.42 0.09 150)']
          : ['samodejni prevod', 'oklch(0.42 0.09 150)'];
  return <span style={{ fontSize: rem(12), color: tone }}>{text}</span>;
}
